import styles from './Sparkline.module.css';

/**
 * A figure's shape, in about 60 pixels.
 *
 * Hand-rolled SVG rather than a charting library: this has no axes, no
 * tooltip, no legend and no responsive container, so Recharts would cost a
 * wrapper, a resize observer and a render cycle to draw one path.
 *
 * Deliberately unlabelled. It answers "rising, falling or flat?" — anything
 * more precise belongs in the timeseries block below it, and putting readable
 * values on a 60px chart invites people to read numbers off it that aren't
 * really there.
 */

interface Props {
  values: number[];
  /** Colour hint; defaults to the accent used for positive series. */
  tone?: 'up' | 'down' | 'flat';
  width?: number;
  height?: number;
  /** Explicit stroke/fill colour (e.g. a --metric-* token); overrides `tone`. */
  color?: string;
}

export function Sparkline({ values, tone = 'flat', width = 64, height = 22, color }: Props) {
  /* Two points is the minimum that can describe a direction. */
  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = width / (values.length - 1);

  /* 1px inset top and bottom so a peak or trough isn't clipped by the stroke. */
  const points = values.map((value, i) => {
    const x = i * step;
    const y = height - 1 - ((value - min) / span) * (height - 2);
    return [x, y] as const;
  });

  const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const area = `${line} L${width} ${height} L0 ${height} Z`;

  return (
    <svg
      className={styles.spark}
      data-tone={color ? undefined : tone}
      style={color ? { color } : undefined}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <path className={styles.area} d={area} />
      <path className={styles.line} d={line} />
    </svg>
  );
}
